import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Search, AlertTriangle, Shield, ShieldAlert, ShieldCheck, HelpCircle, ExternalLink, Loader2 } from "lucide-react";
import axios from "axios";

const API_URL = '';

// Risk level configurations
const RISK_CONFIG = {
  SAFE: {
    color: "var(--sidekick-safe)",
    bgColor: "rgba(34, 197, 94, 0.15)",
    borderColor: "rgba(34, 197, 94, 0.3)",
    icon: ShieldCheck,
    label: "Looks Safe",
  },
  CAUTION_SOFT: {
    color: "var(--sidekick-caution)",
    bgColor: "rgba(245, 158, 11, 0.15)",
    borderColor: "rgba(245, 158, 11, 0.3)",
    icon: Shield,
    label: "Be Careful",
  },
  CAUTION_HARD: {
    color: "var(--sidekick-caution)",
    bgColor: "rgba(245, 158, 11, 0.2)",
    borderColor: "rgba(245, 158, 11, 0.4)",
    icon: AlertTriangle,
    label: "Use Caution",
  },
  DANGER: {
    color: "var(--sidekick-danger)",
    bgColor: "rgba(239, 68, 68, 0.15)",
    borderColor: "rgba(239, 68, 68, 0.3)",
    icon: ShieldAlert,
    label: "High Risk",
  },
  UNKNOWN: {
    color: "var(--sidekick-caution)",
    bgColor: "rgba(156, 163, 175, 0.15)",
    borderColor: "rgba(156, 163, 175, 0.3)",
    icon: HelpCircle,
    label: "Unknown",
  },
};

export default function ResearchPage() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleResearch = async () => {
    if (!url.trim()) {
      setError("Please enter a website address");
      return;
    }

    // Normalize URL
    let normalizedUrl = url.trim();
    if (!normalizedUrl.startsWith("http://") && !normalizedUrl.startsWith("https://")) {
      normalizedUrl = "https://" + normalizedUrl;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await axios.post(`${API_URL}/api/research/site`, {
        url: normalizedUrl,
        user_question: question || "Is this website legit? What should I watch for?",
      });

      setResult(response.data);
    } catch (err) {
      console.error("Research error:", err);
      setError(err.response?.data?.detail || "Failed to research this website. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const riskConfig = result ? RISK_CONFIG[result.risk_level] || RISK_CONFIG.UNKNOWN : null;
  const RiskIcon = riskConfig?.icon || HelpCircle;

  return (
    <div className="research-page" data-testid="research-page">
      {/* Header */}
      <header className="research-header">
        <button 
          className="back-button" 
          onClick={() => navigate("/home")}
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
        </button>
        <h1>Website Research</h1>
      </header>

      {/* Search Form */}
      <div className="research-form">
        <div className="form-group">
          <label htmlFor="url-input">Website Address</label>
          <div className="input-wrapper">
            <Search size={18} className="input-icon" />
            <input
              id="url-input"
              type="text"
              placeholder="example.com or https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleResearch()}
              disabled={loading}
              data-testid="url-input"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="question-input">What would you like to know? (optional)</label>
          <textarea
            id="question-input"
            placeholder="Is this website safe? Should I buy from them?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={loading}
            rows={2}
            data-testid="question-input"
          />
        </div>

        <button
          className="research-button"
          onClick={handleResearch}
          disabled={loading || !url.trim()}
          data-testid="research-button"
        >
          {loading ? (
            <>
              <Loader2 size={20} className="spin" />
              Researching...
            </>
          ) : (
            <>
              <Search size={20} />
              Research This Website
            </>
          )}
        </button>
      </div>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            className="error-message"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            data-testid="error-message"
          >
            <AlertTriangle size={18} />
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Results */}
      <AnimatePresence>
        {result && (
          <motion.div
            className="research-results"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            data-testid="research-results"
          >
            {/* Risk Card */}
            <div 
              className="risk-card"
              style={{
                backgroundColor: riskConfig.bgColor,
                borderColor: riskConfig.borderColor,
              }}
            >
              <div className="risk-header">
                <RiskIcon 
                  size={32} 
                  style={{ color: riskConfig.color }} 
                />
                <div className="risk-info">
                  <span 
                    className="risk-label"
                    style={{ color: riskConfig.color }}
                  >
                    {riskConfig.label}
                  </span>
                  <span className="risk-hostname">{result.hostname}</span>
                </div>
                <div className="risk-score">
                  <span className="score-value">{result.score}</span>
                  <span className="score-label">Risk Score</span>
                </div>
              </div>

              <p className="risk-summary">{result.summary}</p>

              {/* Confidence indicator */}
              <div className="confidence-bar">
                <span className="confidence-label">
                  Confidence: {Math.round(result.confidence * 100)}%
                </span>
                <div className="confidence-track">
                  <div 
                    className="confidence-fill"
                    style={{ 
                      width: `${result.confidence * 100}%`,
                      backgroundColor: riskConfig.color 
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Evidence Section */}
            {result.evidence && result.evidence.length > 0 && (
              <div className="evidence-section">
                <h3>What We Found</h3>
                <ul className="evidence-list">
                  {result.evidence.map((item, idx) => (
                    <li 
                      key={idx}
                      className={`evidence-item ${item.is_risk ? "risk" : "trust"}`}
                    >
                      <span className="evidence-icon">
                        {item.is_risk ? "⚠️" : "✓"}
                      </span>
                      <div className="evidence-content">
                        <strong>{item.label}</strong>
                        <span>{item.detail}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Reasons */}
            {result.reasons && result.reasons.length > 0 && (
              <div className="reasons-section">
                <h3>Why This Rating</h3>
                <ul className="reasons-list">
                  {result.reasons.map((reason, idx) => (
                    <li key={idx}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* What To Do Next */}
            <div className="actions-section">
              <h3>What To Do Next</h3>
              <ul className="actions-list">
                {result.what_to_do_next.map((action, idx) => (
                  <li key={idx}>
                    <span className="action-number">{idx + 1}</span>
                    {action}
                  </li>
                ))}
              </ul>
            </div>

            {/* Metadata */}
            <div className="result-meta">
              {result.gemini_used && (
                <span className="meta-item">
                  AI-powered analysis
                  {result.gemini_latency_ms && ` (${result.gemini_latency_ms}ms)`}
                </span>
              )}
              <span className="meta-item">
                {new Date(result.timestamp).toLocaleString()}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .research-page {
          min-height: 100vh;
          background: var(--sidekick-bg);
          padding: 0 20px 40px;
        }

        .research-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px 0;
          position: sticky;
          top: 0;
          background: var(--sidekick-bg);
          z-index: 10;
        }

        .back-button {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          border: 1px solid var(--sidekick-border);
          background: var(--sidekick-card);
          color: var(--sidekick-text);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .back-button:hover {
          background: var(--sidekick-hover);
          border-color: var(--sidekick-primary);
        }

        .research-header h1 {
          font-size: 24px;
          font-weight: 700;
          color: var(--sidekick-text);
          margin: 0;
        }

        .research-form {
          background: var(--sidekick-card);
          border: 1px solid var(--sidekick-border);
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 24px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group:last-of-type {
          margin-bottom: 24px;
        }

        .form-group label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: var(--sidekick-text);
          margin-bottom: 8px;
        }

        .input-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--sidekick-muted);
        }

        .input-wrapper input {
          width: 100%;
          padding: 14px 14px 14px 44px;
          border: 1px solid var(--sidekick-border);
          border-radius: 12px;
          background: var(--sidekick-bg);
          color: var(--sidekick-text);
          font-size: 16px;
          transition: all 0.2s;
        }

        .input-wrapper input:focus {
          outline: none;
          border-color: var(--sidekick-primary);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }

        .form-group textarea {
          width: 100%;
          padding: 14px;
          border: 1px solid var(--sidekick-border);
          border-radius: 12px;
          background: var(--sidekick-bg);
          color: var(--sidekick-text);
          font-size: 16px;
          resize: vertical;
          font-family: inherit;
          transition: all 0.2s;
        }

        .form-group textarea:focus {
          outline: none;
          border-color: var(--sidekick-primary);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }

        .research-button {
          width: 100%;
          padding: 16px 24px;
          border: none;
          border-radius: 12px;
          background: var(--sidekick-primary);
          color: white;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.2s;
        }

        .research-button:hover:not(:disabled) {
          background: var(--sidekick-primary-hover);
          transform: translateY(-1px);
        }

        .research-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 18px;
          background: rgba(239, 68, 68, 0.15);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 12px;
          color: var(--sidekick-danger);
          font-size: 14px;
          margin-bottom: 24px;
        }

        .research-results {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .risk-card {
          padding: 24px;
          border-radius: 16px;
          border: 2px solid;
        }

        .risk-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 16px;
        }

        .risk-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .risk-label {
          font-size: 18px;
          font-weight: 700;
        }

        .risk-hostname {
          font-size: 14px;
          color: var(--sidekick-muted);
        }

        .risk-score {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 12px 16px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 12px;
        }

        .score-value {
          font-size: 24px;
          font-weight: 700;
          color: var(--sidekick-text);
        }

        .score-label {
          font-size: 10px;
          color: var(--sidekick-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .risk-summary {
          font-size: 16px;
          line-height: 1.6;
          color: var(--sidekick-text);
          margin: 0 0 16px;
        }

        .confidence-bar {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .confidence-label {
          font-size: 12px;
          color: var(--sidekick-muted);
        }

        .confidence-track {
          height: 6px;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 3px;
          overflow: hidden;
        }

        .confidence-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.5s ease;
        }

        .evidence-section,
        .reasons-section,
        .actions-section {
          background: var(--sidekick-card);
          border: 1px solid var(--sidekick-border);
          border-radius: 16px;
          padding: 20px;
        }

        .evidence-section h3,
        .reasons-section h3,
        .actions-section h3 {
          font-size: 16px;
          font-weight: 700;
          color: var(--sidekick-text);
          margin: 0 0 16px;
        }

        .evidence-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .evidence-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px;
          border-radius: 10px;
        }

        .evidence-item.risk {
          background: rgba(239, 68, 68, 0.1);
        }

        .evidence-item.trust {
          background: rgba(34, 197, 94, 0.1);
        }

        .evidence-icon {
          font-size: 16px;
          flex-shrink: 0;
        }

        .evidence-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .evidence-content strong {
          font-size: 14px;
          color: var(--sidekick-text);
        }

        .evidence-content span {
          font-size: 13px;
          color: var(--sidekick-muted);
        }

        .reasons-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .reasons-list li {
          font-size: 14px;
          color: var(--sidekick-text);
          padding-left: 20px;
          position: relative;
        }

        .reasons-list li::before {
          content: "•";
          position: absolute;
          left: 0;
          color: var(--sidekick-muted);
        }

        .actions-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .actions-list li {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          font-size: 15px;
          color: var(--sidekick-text);
        }

        .action-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: var(--sidekick-primary);
          color: white;
          font-size: 12px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .result-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          padding-top: 8px;
        }

        .meta-item {
          font-size: 12px;
          color: var(--sidekick-muted);
        }
      `}</style>
    </div>
  );
}
